import {AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, Output, EventEmitter, forwardRef, OnInit, ChangeDetectorRef, ViewChild} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AutoSizeDirective } from '../../modules/share/auto-size.directive';

@Component({
  selector: 'app-floating-input',
  templateUrl: './floating-input.component.html',
  styleUrls: ['./floating-input.component.css'],
  // Раньше был OnPush: значение может прийти снаружи несколькими путями
  // ([value], ngModel/writeValue, программное обновление из edit-режима),
  // и с OnPush требовалось безукоризненно точно дёргать markForCheck()/
  // detectChanges() в каждой из этих точек — на практике это оказалось
  // хрупко (см. историю правок formValue/writeValue) и приводило к тому,
  // что уже загруженные значения не отображались. Обычная стратегия проще
  // и надёжнее для формы такого размера — перф здесь не критичен.
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FloatingInputComponent),
      multi: true
    }
  ]
})
export class FloatingInputComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {
  @Input() label: string = '';
  // Небольшая цветная плашка справа от текста лейбла (например "ГОСДАННЫЕ" —
  // см. pdf-generate flow, form.component.html). Пусто по умолчанию — не
  // затрагивает остальные использования компонента.
  @Input() badge: string = '';
  @Input() type: string = 'text';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() pattern: string = '';
  @Input() maxlength: number | null = null;
  @Input() name: string = '';
  @Input() id: string = '';
  @Input() rows: number = 1;
  @Input() inputClass: string | string[] | Set<string> | { [klass: string]: any } = '';
  @Input() prefix: string = '';
  @Input() mask: string = '';
  @Input() dropSpecialCharacters: boolean = true;

  @Output() keypress = new EventEmitter<any>();
  @Output() paste = new EventEmitter<any>();
  @Output() change = new EventEmitter<any>();
  @Output() keyup = new EventEmitter<any>();

  private _value: any = '';

  @ViewChild('wrapperEl', { static: true }) private wrapperEl!: ElementRef<HTMLElement>;
  @ViewChild('labelEl', { static: true }) private labelEl!: ElementRef<HTMLElement>;
  @ViewChild(AutoSizeDirective) private autoSize?: AutoSizeDirective;
  private labelResizeObserver?: ResizeObserver;

  constructor(private cdRef: ChangeDetectorRef, private zone: NgZone) {}

  @Input()
  set value(val: any) {
    if (val !== this._value) {
      this._value = val;
      // OnPush: значение может прийти не только от пользователя (onInput/
      // writeValue), но и напрямую через [value] — например, когда форма
      // программно заполняется данными уже созданного контракта (edit-режим).
      // Без markForCheck() компонент в этом случае не перерисуется.
      this.cdRef.markForCheck();
    }
  }

  get value(): any {
    return this._value;
  }

  isFocused: boolean = false;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit() {
    if (!this.id && this.name) {
      this.id = `field_${this.name}`;
    }
  }

  // У textarea лейбл переносится на несколько строк (см. .is-textarea в css),
  // но он position: absolute и сам места не занимает — поэтому его реальную
  // высоту отдаём в css переменной --floating-label-height, от неё считаются
  // padding-top/min-height textarea. После смены отступа пересчитываем и
  // авто-высоту, иначе нижняя строка значения обрежется.
  ngAfterViewInit() {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.labelResizeObserver = new ResizeObserver(() => {
        const height = this.labelEl.nativeElement.offsetHeight;
        if (!height) {
          return;
        }
        this.wrapperEl.nativeElement.style.setProperty('--floating-label-height', `${height}px`);
        this.autoSize?.adjust();
      });
      this.labelResizeObserver.observe(this.labelEl.nativeElement);
    });
  }

  ngOnDestroy() {
    this.labelResizeObserver?.disconnect();
  }

  get hasValue(): boolean {
    return this.value !== null && this.value !== undefined && this.value !== '';
  }

  get isTextarea(): boolean {
    return this.type === 'textarea';
  }

  onFocus() {
    this.isFocused = true;
  }

  onBlur() {
    this.isFocused = false;
    this.onTouched();
  }

  onInput(event: any) {
    const formatted = this.applyMask(event.target.value);
    event.target.value = formatted;
    this._value = formatted;
    this.onChange(this._value);
    this.cdRef.detectChanges();
  }

  onKeypress(event: any) {
    this.keypress.emit(event);
  }

  onPaste(event: any) {
    this.paste.emit(event);
  }

  onChange_event(event: any) {
    this.change.emit(event);
  }

  onKeyup(event: any) {
    this.keyup.emit(event);
  }

  writeValue(value: any): void {
    if ((value === null || value === undefined) && this.hasValue) {
      // NgModel синхронно вызывает writeValue(control.value) во время своей
      // ВНУТРЕННЕЙ настройки (setUpControl) — на этот момент его собственный
      // FormControl ещё не синхронизирован с текущей моделью и его value
      // всегда null, даже если снаружи ([value] или предыдущий корректный
      // writeValue) сюда уже пришло реальное значение. Не даём этому
      // артефакту стирать уже выставленное значение.
      return;
    }
    const formatted = this.applyMask(value);
    if (formatted !== this._value) {
      this._value = formatted;
      this.cdRef.detectChanges();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private applyMask(value: any): string {
    if (!this.mask) {
      return value ?? '';
    }

    const digits = this.extractDigits(value);
    if (!digits.length) {
      return '';
    }

    let formatted = '';
    let digitIndex = 0;
    for (const char of this.mask) {
      if (char === '0') {
        if (digitIndex < digits.length) {
          formatted += digits[digitIndex++];
        } else {
          break;
        }
      } else if (digits.length > 0) {
        formatted += char;
      }
    }

    formatted = formatted.replace(/\s+$/, '');
    const prefixPart = this.buildPrefix(digits.length > 0);
    return prefixPart ? `${prefixPart}${formatted}` : formatted;
  }

  private extractDigits(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    const digits = value.toString().replace(/\D/g, '');
    const prefixDigits = this.prefix?.replace(/\D/g, '') ?? '';

    if (prefixDigits && digits.startsWith(prefixDigits)) {
      return digits.substring(prefixDigits.length);
    }

    return digits;
  }

  private buildPrefix(hasDigits: boolean): string {
    if (!hasDigits || !this.prefix) {
      return '';
    }

    const trimmed = this.prefix.trim();
    if (!trimmed) {
      return '';
    }

    return trimmed.startsWith('+') ? `${trimmed} ` : `+${trimmed} `;
  }
}
